/**
 * Home Analytics — React Query hooks
 *
 * One query per dashboard section so each card loads, errors and refetches
 * independently. Each hook resolves its use case through `HomeAnalyticsFactory`
 * (DI, like the rest of the codebase) and runs it via React Query. Callers pass
 * `enabled` (derived from the client's enabled modules) so disabled modules
 * never hit the network.
 */

import {useMemo} from 'react';
import {useQuery, type UseQueryResult} from '@tanstack/react-query';
import {HomeAnalyticsFactory} from '../store/homeAnalyticsFactory';
import type {
  InventoryProductsAnalytics,
  StoresUsersAnalytics,
  VisitsAnalytics
} from '../models/homeAnalytics.model';

/** Stats change slowly; cache for 5 minutes before considering stale. */
const STALE_TIME = 5 * 60 * 1000;

export const HOME_ANALYTICS_KEYS = {
  inventoryProducts: ['home-analytics', 'inventory-products'] as const,
  visits: ['home-analytics', 'visits'] as const,
  storesUsers: ['home-analytics', 'stores-users'] as const
};

export const useInventoryProductsAnalytics = (
  enabled: boolean
): UseQueryResult<InventoryProductsAnalytics> => {
  const useCase = useMemo(
    () => HomeAnalyticsFactory.createGetInventoryProductsAnalyticsUseCase(),
    []
  );
  return useQuery({
    queryKey: HOME_ANALYTICS_KEYS.inventoryProducts,
    queryFn: () => useCase.execute(),
    enabled,
    staleTime: STALE_TIME
  });
};

export const useVisitsAnalytics = (
  enabled: boolean
): UseQueryResult<VisitsAnalytics> => {
  const useCase = useMemo(
    () => HomeAnalyticsFactory.createGetVisitsAnalyticsUseCase(),
    []
  );
  return useQuery({
    queryKey: HOME_ANALYTICS_KEYS.visits,
    queryFn: () => useCase.execute(),
    enabled,
    staleTime: STALE_TIME
  });
};

export const useStoresUsersAnalytics = (
  enabled: boolean
): UseQueryResult<StoresUsersAnalytics> => {
  const useCase = useMemo(
    () => HomeAnalyticsFactory.createGetStoresUsersAnalyticsUseCase(),
    []
  );
  return useQuery({
    queryKey: HOME_ANALYTICS_KEYS.storesUsers,
    queryFn: () => useCase.execute(),
    enabled,
    staleTime: STALE_TIME
  });
};
